<template>
  <div>
    <Button @click="onClickGoLogin" class="w-8 h-8 p-0"><span class="iconfont iconfanhui"></span></Button>
    <h2 class="text-center text-[28px] font-semibold">绑定手机号</h2>
    <p class="text-center text-base mt-1">你的{{ oauthType === 'github' ? ' GitHub ' : '微信' }}账号暂未绑定和鲸账号</p>
    <Form class="my-6 oauth-message-form">
      <FormItem>
        <Input v-model="phone" placeholder="请输入手机号" @on-change="error = ''">
          <template #prepend>
            <Select v-model="country" @on-change="error = ''">
              <Option v-for="country in Countries" :key="country.code" :value="country.code" :label="`+${country.code}`">
                <span>{{ country.name_cn }} +{{ country.code }}</span>
              </Option>
            </Select>
          </template>
        </Input>
      </FormItem>
      <FormItem>
        <div id="oauth-captcha-container">
          <Captcha
            ref="captchaInstance"
            container="#oauth-captcha-container"
            @success="onCaptchaSuccess"
            @fail="onCaptchaFail"
            @error="onCaptchaError"
          ></Captcha>
        </div>
      </FormItem>
      <FormItem v-if="sent">
        <Input v-model="code" placeholder="请输入 6 位短信验证码">
          <template #append>
            <div v-if="seconds > 0" class="px-2 text-disabled select-none">{{ seconds }} 秒后重新发送</div>
            <div v-else class="bg-primary-main text-white leading-[48px] cursor-pointer select-none" @click="onClickSendMessage">发送验证码</div>
          </template>
        </Input>
      </FormItem>
      <FormItem v-if="error" class="min-h-0">
        <Alert type="error" show-icon class="m-0">{{ error }}</Alert>
      </FormItem>
      <FormItem>
        <Button @click="onClickAuthAndLogin" type="primary" size="large" long class="h-12 rounded-md">绑定并登录</Button>
      </FormItem>
      <p class="text-center text-secondary-text text-xs">登录即代表同意和鲸平台<a href="/static/terms.html" target="_blank" class="text-sub-color">《用户协议》</a>、<a href="/static/privacy.html" target="_blank" class="text-sub-color">《隐私保护指引》</a></p>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
// TODO vuex typings
// @ts-ignore
import { useStore } from 'vuex'
import axios from 'axios'
import getErrorMessage from '@/utils/get-error-message'
import getAWSCTokens from '@/utils/get-awsc-tokens'
import useLoginCheck from '@/composables/login-check.ts'
import Countries from '@/constants/country-list'
import Captcha from '@/components/shared/captcha.vue'

type CaptchaExpose = {
  init: (forceRecreate?: boolean) => void
}

type CaptchaVerifyData = {
  captchaVerifyParam: string
  sceneId: string
}

const router = useRouter()
const store = useStore()

const oauthType = computed(() => store.state.oauthType)
const country = ref('86')
const phone = ref('')
const code = ref('')
const sent = ref(false)
const error = ref('')
const captchaInstance = ref<CaptchaExpose | null>(null)
const captchaData = ref<CaptchaVerifyData | null>(null)
const accountStatus = ref('')

const seconds = ref(60)
const timer = ref<ReturnType<typeof setInterval> | 0>(0)

const onClickGoLogin = () => {
  router.push({ name: 'login' })
}

const refreshCaptcha = (forceRecreate = false) => {
  captchaInstance.value?.init(forceRecreate)
}

const onCaptchaSuccess = (data: CaptchaVerifyData) => {
  captchaData.value = data
  error.value = ''
  onClickSendMessage()
}

const onCaptchaFail = () => {
  captchaData.value = null
  error.value = '请先完成滑动验证'
}

const onCaptchaError = (errorCode: unknown) => {
  captchaData.value = null
  if (errorCode === 'missing-scene-id' || errorCode === 'captcha-sdk-not-ready') {
    error.value = '验证码加载失败，请刷新后重试'
    return
  }
  error.value = '验证码服务异常，请稍后重试'
}

const isLegacyCaptchaSchemaError = (err: unknown) => {
  const message = getErrorMessage(err).toLowerCase()
  const hasUnknownField = message.includes('unknown field') || message.includes('未知字段')
  const hasCaptchaField = message.includes('captchaverifyparam') || message.includes('sceneid')
  return hasUnknownField && hasCaptchaField
}

const sendRegisterSmsCodeWithCompatibility = async (basePayload: { phone: string; countryCode: string }) => {
  if (!captchaData.value) {
    throw new Error('captcha-data-missing')
  }

  const enhancedPayload = {
    ...basePayload,
    captchaVerifyParam: captchaData.value.captchaVerifyParam,
    sceneId: captchaData.value.sceneId,
    scene: captchaData.value.sceneId,
  }

  try {
    return await axios.post('/api/auth/sendRegisterSmsCode', enhancedPayload)
  } catch (err) {
    if (!isLegacyCaptchaSchemaError(err)) {
      throw err
    }
    return await axios.post('/api/auth/sendRegisterSmsCode', basePayload)
  }
}

const onClickSendMessage = async () => {
  if (seconds.value > 0 && sent.value) {
    return
  }

  if (!captchaData.value) {
    error.value = '请先完成验证'
    return
  }
  if (!phone.value.match(/^\d{7,}$/)) {
    refreshCaptcha(true)
    captchaData.value = null
    error.value = '请输入有效的手机号'
    return
  }

  try {
    const payloadBase = {
      phone: phone.value,
      countryCode: country.value,
    }
    const { data } = await sendRegisterSmsCodeWithCompatibility(payloadBase)
    accountStatus.value = data?.status || ''
    sent.value = true
    error.value = ''
    seconds.value = 60

    if (timer.value) {
      clearInterval(timer.value)
    }

    timer.value = setInterval(() => {
      if (seconds.value <= 0) {
        refreshCaptcha(true)
        clearInterval(timer.value)
        timer.value = 0
        captchaData.value = null
        sent.value = false
        error.value = ''
        return
      }
      seconds.value = seconds.value - 1
    }, 1000)
  } catch (e: unknown) {
    refreshCaptcha(true)
    seconds.value = 0
    sent.value = false
    captchaData.value = null
    error.value = getErrorMessage(e)
  }
}

const onClickAuthAndLogin = async () => {
  if (!code.value) {
    error.value = '请输入验证码'
    return
  }

  if (!sent.value) {
    error.value = '请先获取验证码'
    return
  }

  const { webUmidToken, uaToken } = getAWSCTokens()
  const payload: { [key: string]: string } = {
    webUmidToken,
    uaToken,
    code: code.value
  }
  try {
    if (accountStatus.value === 'ACTIVE') {
      // 登录
      if (store.state.oauthState) payload.oauth = store.state.oauthState
      const { data } = await axios.post('/api/auth/loginBySmsCode', payload)
      if (data?.code === 4) {
        error.value = data?.message || ''
        return
      }
      useLoginCheck()
    }
    if (accountStatus.value === 'NOT_FOUND') {
      // 注册
      if (store.state.oauthState) payload.oauth = store.state.oauthState
      const redirect = new URLSearchParams(window.location.search)?.get('redirect') || '';
      if (redirect) payload.registerFrom = redirect
      await axios.post('/api/users/registerByPhone', payload)
      useLoginCheck()
    }
    if (accountStatus.value === 'BANNED') {
      error.value = '此账号已被封禁'
    }
  } catch (e: unknown) {
    error.value = getErrorMessage(e)
  }
}

onUnmounted(() => {
  if (timer.value) {
    clearInterval(timer.value)
  }
})
</script>

<style lang="scss">
.oauth-message-form {
  .ivu-form-item {
    min-height: 48px;
    margin-bottom: 16px;
  }
  .ivu-input-wrapper {
    input {
      height: 48px !important;
    }
  }
  .ivu-input-group-with-prepend {
    .ivu-input-group-prepend {
      width: 80px;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
    input {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }
  .ivu-input-group-with-append {
    .ivu-input-group-append {
      min-width: 90px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      overflow: hidden;
      padding: 0px;
      height: 48px;
    }
    input {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }
}
</style>
